"use client";

import { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useLocale } from "@/content/i18n/context";
import { t } from "@/content/i18n/messages";
import { cn } from "@/lib/utils";

export interface ServiceFaqItem {
  question: string;
  answer: string;
}

export interface ServiceFaqProps {
  items: ServiceFaqItem[];
  className?: string;
}

/**
 * Shared FAQ accordion — hairline divided rows with serif questions.
 * Pages keep their own <StructuredData type="faq"> co-located alongside this.
 */
export default function ServiceFaq({ items, className = "" }: ServiceFaqProps) {
  const locale = useLocale();
  const prefersReducedMotion = useReducedMotion();
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  return (
    <motion.div
      initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.25, ease: [0.21, 0.6, 0.35, 1] }}
      className={cn("divide-y divide-border border-y border-border", className)}
    >
      {items.map((item, index) => {
        const isExpanded = expandedIndex === index;

        return (
          <div key={item.question}>
            <button
              type="button"
              onClick={() => setExpandedIndex(isExpanded ? null : index)}
              aria-expanded={isExpanded}
              className="flex w-full items-center justify-between gap-4 py-5 text-left"
            >
              <span className="font-display text-base font-semibold text-neutral-emphasis lg:text-lg">
                {t(locale, item.question)}
              </span>
              <ChevronDown
                className={cn(
                  "h-5 w-5 flex-shrink-0 text-neutral-muted transition-transform duration-300",
                  isExpanded && "rotate-180",
                )}
                aria-hidden
              />
            </button>
            <div
              className={cn(
                "grid transition-all duration-300",
                isExpanded
                  ? "grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0",
              )}
            >
              <div className="overflow-hidden">
                <p className="pb-6 text-sm leading-relaxed text-neutral-muted lg:text-base">
                  {t(locale, item.answer)}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </motion.div>
  );
}
